<template>
    <div>
        <CreateBlogPostDialog v-model="showAddDialog" @created="handleBlogPostCreated" />
        <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />

        <EditBlogPostDialog v-model="showEditDialog" :blogpost="selectedBlogPost" @updated="handleBlogPostUpdated" />

        <DeleteBlogPostDialog v-model="showDeleteDialog" :blogpost="selectedBlogPostToDelete"
            @deleted="handleBlogPostDeleted" />

        <UnpublishBlogPostDialog v-model="showUnpublishDialog" :blogpost="selectedBlogPostToUnpublish"
            @unpublished="handleBlogPostUnpublished" />
        <div class="table-container">
            <v-card flat>
                <v-data-table :headers="headers" :items="BlogPosts" :items-per-page="50"
                    class="elevation-0 custom-data-table" :no-data-text="noDataText" :height="tableHeight" fixed-header
                    :loading="tableLoading" density="compact" @click:row="handleRowClick">
                    <template v-slot:top>
                        <div class="d-flex justify-space-between align-center pa-4">
                            <span class="text-h5">Blog Post</span>
                            <v-btn color="primary" prepend-icon="mdi-plus" @click="openAddDialog">Add
                                Blog</v-btn>
                        </div>
                    </template>
                    <template v-slot:[`item.blog_content`]="{ item }">
                        <div class="testimonial-message-preview">
                            {{ truncateMessage(item.blog_content) }}
                        </div>
                    </template>
                    <template v-slot:[`item.published_on`]="{ item }">
                        {{ formatTimestamp(item.published_on) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn icon size="small" variant="text" v-bind="props" @click.stop>
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item title="Edit" @click.stop="openEditDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="primary" size="small">mdi-pencil</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="item.blog_status === 'PUBLISHED'" title="Unpublish"
                                    @click.stop="openUnpublishDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="warning" size="small">mdi-eye-off</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="item.blog_status === 'UNPUBLISHED'" title="Publish"
                                    @click.stop="openPublishDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="success" size="small">mdi-eye</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="item.blog_status === 'UNPUBLISHED'" title="Delete"
                                    @click.stop="openDeleteDialog(item)">
                                    <template v-slot:prepend>
                                        <v-icon color="error" size="small">mdi-delete</v-icon>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-card>
        </div>
    </div>
</template>

<script>
import S3AmplifyMixin from "@/mixins/S3Amplify";
import { edit_blog } from "@/graphql/mutations";
import EditBlogPostDialog from '@/components/BlogPost/Dailogs/EditBlogPostDialog.vue';
import GetBlogPosts from '@/mixins/BlogPost/GetBlogPosts';
import CreateBlogPostDialog from '@/components/BlogPost/Dailogs/CreateBlogPostDialog.vue';
import DeleteBlogPostDialog from '@/components/BlogPost/Dailogs/DeleteBlogPostDialog.vue';
import UnpublishBlogPostDialog from '@/components/BlogPost/Dailogs/UnpublishBlogPostDialog.vue';
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";


const client = generateClient();

export default {
    name: "BlogPostPage",
    mixins: [GetBlogPosts, S3AmplifyMixin],
    components: {
        CreateBlogPostDialog,
        EditBlogPostDialog,
        DeleteBlogPostDialog,
        UnpublishBlogPostDialog,
        SnackbarComponent
    },
    data() {
        return {
            tableLoading: false,
            windowHeight: window.innerHeight,
            currentPage: 1,
            headers: [
                {
                    title: "Author",
                    key: "blog_author_name",
                    sortable: true,
                },
                {
                    title: "Blog Title",
                    key: "blog_main_title",
                    sortable: true,
                },
                {
                    title: "Description",
                    key: "blog_content",
                    sortable: true,
                },
                {
                    title: "Status",
                    key: "blog_status",
                    sortable: true,
                },
                {
                    title: "Published Date",
                    key: "published_on",
                    sortable: true,
                },
                {
                    title: "Actions",
                    key: "actions",
                    sortable: false,
                },
            ],
            showAddDialog: false,
            showEditDialog: false,
            showDeleteDialog: false,
            showUnpublishDialog: false,
            showViewsDialog: false,
            snackbar: {
                show: false,
                message: "",
                color: "success",
            },
            selectedBlogPost: null,
            selectedBlogPostToDelete: null,
            selectedBlogPostToUnpublish: null,
        };
    },
    computed: {
        BlogPosts() {
            return this.BlogPostsList || [];
        },
        noDataText() {
            return "No Blog Posts found. Click 'Add Blog Posts' to add one.";
        },
        tableHeight() {
            return this.windowHeight - 170;
        },
    },
    async mounted() {
        this.updateWindowHeight();
        window.addEventListener("resize", this.updateWindowHeight);
        this.tableLoading = true;
        await this.fetchBlogPosts();
        await this.loadBlogDescriptions();
        const session = await fetchAuthSession();

        console.log(session);
        this.tableLoading = false;
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.updateWindowHeight);
    },
    methods: {
        async loadBlogDescriptions() {
            for (const blog of this.BlogPostsList) {
                try {
                    console.log("S3 Path:", blog.blog_content_s3_path);

                    const fileContent = await this.getFileFromS3(blog.blog_content_s3_path);

                    console.log("Raw file:", fileContent);

                    // Since the file contains plain text
                    blog.blog_content = fileContent;

                    console.log("Description:", blog.blog_content);

                } catch (err) {
                    console.error("Failed to load blog description:", err);
                    blog.blog_content = "";
                }
            }
        },
        async openPublishDialog(item) {
            try {
                const input = {
                    blog_id: item.blog_id,
                    blog_status: "PUBLISHED",
                };

                const response = await client.graphql({
                    query: edit_blog,
                    variables: { input },
                });

                const responseData = JSON.parse(
                    response.data.edit_blog
                );

                if (responseData.status === "SUCCESS") {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Testimonial published successfully!",
                        color: "success",
                    };

                    await this.fetchBlogPosts();
                    await this.loadBlogDescriptions();
                } else {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Failed to publish testimonial",
                        color: "error",
                    };
                }
            } catch (error) {
                console.error(error);

                this.snackbar = {
                    show: true,
                    message:
                        error?.errors?.[0]?.message ||
                        error?.message ||
                        "An error occurred while publishing testimonial",
                    color: "error",
                };
            }
        },
        openDeleteDialog(item) {
            this.selectedBlogPostToDelete = item;
            this.showDeleteDialog = true;
        },
        handleRowClick(event, { item }) {
            this.openEditDialog(item);
        },
        openEditDialog(item) {
            this.selectedBlogPost = item;
            this.showEditDialog = true;
        },
        openUnpublishDialog(item) {
            this.selectedBlogPostToUnpublish = item;
            this.showUnpublishDialog = true;
        },
        async handleBlogPostCreated() {
            this.tableLoading = true;
            await this.fetchBlogPosts();
            await this.loadBlogDescriptions();
            this.tableLoading = false;
        },

        async handleBlogPostUpdated() {
            this.tableLoading = true;
            await this.fetchBlogPosts();
            await this.loadBlogDescriptions();
            this.tableLoading = false;
        },

        async handleBlogPostDeleted() {
            this.tableLoading = true;
            await this.fetchBlogPosts();
            await this.loadBlogDescriptions();
            this.tableLoading = false;
        },

        async handleBlogPostUnpublished() {
            this.tableLoading = true;
            await this.fetchBlogPosts();
            await this.loadBlogDescriptions();
            this.tableLoading = false;
        },
        formatTimestamp(timestamp) {
            const numeric = Number(timestamp);
            if (isNaN(numeric)) return "";
            const ms = numeric < 1e12 ? numeric * 1000 : numeric;
            const date = new Date(ms);
            if (isNaN(date.getTime())) return "";
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                // hour: "2-digit",
                // minute: "2-digit",
                // hour12: true,
            });
        },
        truncateMessage(message) {
            if (!message) return "";
            // Strip HTML tags for preview
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = message;
            const textContent = tempDiv.textContent || tempDiv.innerText || "";
            // Return first line (approximately 80 characters) with ellipsis
            if (textContent.length > 50) {
                return textContent.substring(0, 50) + "...";
            }
            return textContent;
        },
        getFullMessageText(message) {
            if (!message) return "";
            // Strip HTML tags to get plain text for tooltip
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = message;
            return tempDiv.textContent || tempDiv.innerText || "";
        },
        updateWindowHeight() {
            this.windowHeight = window.innerHeight;
        },
        openAddDialog() {
            this.showAddDialog = true;
        },
    },
};
</script>

<style scoped></style>